import { useEffect, useRef, useState, type ReactNode } from "react";
import { QUICK_SETTINGS, SETTING_CHOICES, SETTING_ROWS, type QuickSetting, type Settings } from "../game/settings";
import { ICON, ICON_ON } from "./buttons";
import { choiceDetail } from "./choice-detail";
import { PANEL } from "./theme";

/** A quick setting's choices: its value, name and one-line pitch (the same as the Settings lists). */
interface Choice {
  value: string;
  label: string;
  detail: string;
}

/** One list in a panel: a setting and its choices (`SETTING_ROWS`' quick ones). */
const QUICK: Record<QuickSetting, { title: string; choices: readonly Choice[] }> = Object.fromEntries(
  QUICK_SETTINGS.map((key) => {
    const labels: Readonly<Record<string, string>> = SETTING_ROWS[key].labels;
    const choices = SETTING_CHOICES[key].map((id): Choice => ({ value: id, label: labels[id]!, detail: choiceDetail(key, id) ?? "" }));
    return [key, { title: SETTING_ROWS[key].label, choices }];
  }),
) as Record<string, { title: string; choices: readonly Choice[] }>;

/**
 * The icons in the row, each opening a panel of one or more lists. A
 * one-list panel closes on a pick; the trick effects' panel (#118) holds
 * four and stays open, so you can set several.
 */
const PANELS: { id: string; title: string; icon: ReactNode; lists: readonly QuickSetting[] }[] = [
  {
    id: "artStyle",
    title: "Art style",
    icon: (
      // A palette.
      <path d="M12 3a9 9 0 1 0 0 18c1.1 0 1.6-.8 1.6-1.6 0-.9-.7-1.3-.7-2.1 0-.9.7-1.5 1.6-1.5H17a4 4 0 0 0 4-4C21 6.6 17 3 12 3Zm-5 9.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Zm3-4a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Zm5 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Z" />
    ),
    lists: ["artStyle"],
  },
  {
    id: "bailStyle",
    title: "Bail style",
    icon: (
      // A burst.
      <path d="m12 2 2.2 5.3L20 5l-2.3 5.6L23 12l-5.3 1.9L20 19l-5.8-2.3L12 22l-2.2-5.3L4 19l2.3-5.1L1 12l5.3-1.4L4 5l5.8 2.3Z" />
    ),
    lists: ["bailStyle"],
  },
  {
    id: "effects",
    title: "Trick effects",
    icon: (
      // Sparkles: a big four-point star and a small one.
      <path d="M9 2c.6 3.9 2.6 5.9 6.5 6.5C11.6 9.1 9.6 11.1 9 15c-.6-3.9-2.6-5.9-6.5-6.5C6.4 7.9 8.4 5.9 9 2Zm8.5 10c.4 2.4 1.6 3.6 4 4-2.4.4-3.6 1.6-4 4-.4-2.4-1.6-3.6-4-4 2.4-.4 3.6-1.6 4-4Z" />
    ),
    lists: ["grindSparks", "landingFx", "airTrail", "finisher"],
  },
  {
    id: "camera",
    title: "Camera",
    icon: (
      // A camera.
      <path d="M4 7h3l2-2.5h6L17 7h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2Zm8 11a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Zm0-2.2a2.3 2.3 0 1 1 0-4.6 2.3 2.3 0 0 1 0 4.6Z" />
    ),
    lists: ["camera"],
  },
];

/**
 * Keeps a click from taking focus: the icons and their lists are for the
 * mouse and fingers, so the keyboard and pad stay on skating (a focused
 * button would eat the next Space or Enter).
 */
const keepFocus = (e: { preventDefault: () => void }) => e.preventDefault();

/**
 * Quick settings on the HUD (#117): a round icon per panel (art style,
 * bail style, trick effects, camera), the same 44 px targets as the rest
 * of the row. Tapping one opens its lists, every choice with its pitch;
 * picking one applies it once, and a tap anywhere else closes the panel.
 * The game keeps running throughout. While a new art style builds, its
 * icon says so ("Applying…"). Settings keeps its rows as well.
 */
export function QuickSettings({
  settings,
  applying,
  onPick,
}: {
  settings: Settings;
  applying: boolean;
  onPick: <K extends QuickSetting>(key: K, value: Settings[K]) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const outside = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(null);
    };
    window.addEventListener("pointerdown", outside, true);
    return () => window.removeEventListener("pointerdown", outside, true);
  }, [open]);

  const panel = PANELS.find((p) => p.id === open) ?? null;
  const single = panel?.lists.length === 1;

  const list = (key: QuickSetting) => (
    <div key={key} role="listbox" aria-label={QUICK[key].title}>
      <div className="px-2 pb-1 text-center text-xs font-black uppercase italic tracking-widest">{QUICK[key].title}</div>
      {QUICK[key].choices.map((c) => {
        const current = settings[key] === c.value;
        return (
          <button
            key={c.value}
            type="button"
            role="option"
            aria-selected={current}
            tabIndex={-1}
            onPointerDown={keepFocus}
            onMouseDown={keepFocus}
            onClick={() => {
              if (single) setOpen(null);
              if (!current) onPick(key, c.value as Settings[typeof key]);
            }}
            className={`block w-full rounded-md px-2 py-1.5 text-center ${current ? "bg-accent text-on-accent" : "hover:bg-surface-soft"}`}
          >
            <span className="block text-sm font-black uppercase italic">
              {c.label}
              {current && <span className="ml-2 text-xs not-italic">✓</span>}
            </span>
            <span className="block text-xs font-medium leading-tight">{c.detail}</span>
          </button>
        );
      })}
    </div>
  );

  const card = `${PANEL} pointer-events-auto absolute right-0 top-full mt-2 max-h-[75dvh] w-[min(90vw,20rem)] overflow-y-auto p-2 text-left`;
  return (
    <div ref={root} className="relative flex items-center gap-2" data-quick-settings>
      {PANELS.map((p) => {
        const busy = p.id === "artStyle" && applying;
        const label = busy ? "Applying…" : p.title;
        return (
          <button
            key={p.id}
            type="button"
            tabIndex={-1}
            aria-label={label}
            title={label}
            aria-haspopup={p.lists.length === 1 ? "listbox" : "dialog"}
            aria-expanded={open === p.id}
            onPointerDown={keepFocus}
            onMouseDown={keepFocus}
            onClick={() => setOpen((o) => (o === p.id ? null : p.id))}
            className={`${open === p.id ? ICON_ON : ICON}${busy ? " animate-pulse" : ""}`}
          >
            <svg aria-hidden viewBox="0 0 24 24" className="size-5" fill="currentColor">
              {p.icon}
            </svg>
          </button>
        );
      })}
      {panel &&
        (single ? (
          <div className={card}>{list(panel.lists[0]!)}</div>
        ) : (
          <div role="dialog" aria-label={panel.title} className={`${card} space-y-2`}>
            {panel.lists.map(list)}
          </div>
        ))}
    </div>
  );
}
